import React, {useMemo, useState} from 'react';
import PropTypes from "prop-types";
import Proposal, {ProposalSubject, ProposalTab} from "./Proposal";
import MatchScoreEditTab from "./MatchScoreEditTab";
import CoverLetterEditTab from "./CoverLetterEditTab";
import AvailabilityRateEditTab from "./AvailabilityRateEditTab";
import {useQueryClient} from '@tanstack/react-query';
import {TalentOppQuickSummary, TeamOppQuickSummary} from "../talent-opportunities/OppQuickSummary";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {useOpportunity} from "../../providers/OpportunityProvider";
import JobProfileResumeModal from "../talent-job-board/components/JobProfileResumeModal";
import {useAuth} from "../../providers/AuthProvider";
import JobProfileSummaryModal from "../talent-job-board/components/JobProfileSummaryModal";
import TeamMemberPayoutEditTab from "./TeamMemberPayoutEditTab";
import FixedFeePayoutEditTab from "./FixedFeePayoutEditTab";
import {Modal} from "antd";
import FixedRetainerPayoutEditTab from "./FixedRetainerPayoutEditTab";

const OppProposalWalkthrough = ({
                                    onClose = () => true, onComplete = () => true,
                                    showSteps, hideHeader, hideSubject, ...props
                                }) => {

    const {
        opportunity,
        onUpdate,
        onShowInterest,
        onRequest,
        changeProps,
        isDisabled,
        onRemoveInterest
    } = useOpportunity();


    const isIndividualOpp = opportunity.isInterested() || opportunity.isProposingAsIndividual();
    const isTeamOpp = opportunity.isProposingWithTeam();
    const queryClient = useQueryClient();
    const location = useLocation();
    const navigate = useNavigate();
    const getTabsForOpp = () => {
        let tabs = {
            "Role & Payout": isTeamOpp,
            "Cover Letter": true,
            "Payout": isIndividualOpp && opportunity?.isFixedFee(),
            "Pricing": isIndividualOpp && opportunity?.isFixedRetainer(),
            "Availability & Rate": isIndividualOpp && opportunity?.isTimeMaterials(),
            "My Match Score": isIndividualOpp,
        };

        return TB.reduce(tabs, (result, value, key) => {
            if (value) {
                result.push(key)
            }
            return result
        }, []);
    }

    const tabs = useMemo(() => getTabsForOpp(), [opportunity?.id]);

    const [activeTab, setActiveTab] = useState(() => TB.first(tabs));

    const activeTabIndex = useMemo(() => tabs.indexOf(activeTab), [activeTab]);

    const [completedTab, setCompletedTab] = useState(0);


    const steps = isIndividualOpp ? [
        'Cover Letter - highlight your background and relevant experience',
        opportunity.is_fixed_fee_contract ? 'Payout & Estimated Hours' : 'Bill Rate and Availability',
        'Match Score - update your skills based upon client project needs',
    ] : ['Role & Payout - review your proposal', 'Cover Letter - highlight your background and relevant experience']

    const goToNextTab = async () => {
        let tab = completedTab;

        console.log('goToNextTab', completedTab, tab)
        if (tab <= activeTabIndex) tab++;

        console.log('goToNextTab', activeTabIndex, tab)
        await setCompletedTab(tab);
        if (tabs[tab]) {
            await setActiveTab(tabs[tab]);
        } else {
            onComplete(opportunity);
        }
    }

    const updateOpp = async (params) => {
        if (params) {
            let updateParams = {...params};

            if (!opportunity.job_candidate?.talent_proposed_at) {
                updateParams.job_candidate = {
                    ...(updateParams.job_candidate || {}),
                    talent_proposed_at: new Date()
                };
            }
            let updatedOpp;
            if (!opportunity?.id && !opportunity?.project_position?.has_shown_interest_in_job) {
                updatedOpp = await onShowInterest(opportunity.project_position, updateParams.job_candidate);
                if (!updatedOpp) {
                    return;
                }
            } else {
                updatedOpp = await onUpdate(updateParams);

                if (!updatedOpp) {
                    return;
                }

                TB.sysAlert('talent.opportunity_proposal_save_btn_click')
            }

            console.log('updateOpp', completedTab);
        }

        return goToNextTab();
    }

    const getStepActions = (info, onSubmit = updateOpp) => {
        return (
            <React.Fragment>
                <div className="proposal-tab-header block sm:flex flex-row items-start">
                    <span className="mb-0 mr-3">
                        {info}
                    </span>
                    <div className="proposal-actions flex flex-row items-center gap-4 my-6 sm:my-0">
                        <button
                            disabled={isDisabled}
                            onClick={() => onSubmit()} className="btn btn-primary whitespace-no-wrap">
                            Save & Continue
                        </button>
                        {(opportunity.isInterested()) ? (
                            <button
                                disabled={isDisabled}
                                onClick={async () => {
                                    let relatedJob = await onRemoveInterest();
                                    if (relatedJob) {
                                        return onClose(relatedJob);
                                    }
                                }} className="btn btn-link relative top-[unset] whitespace-no-wrap">
                                Withdraw Interest
                            </button>
                        ) : (
                            <button
                                disabled={isDisabled}
                                onClick={async () => {
                                    return onClose();
                                }} className="btn btn-link relative top-[unset] whitespace-no-wrap">
                                Cancel
                            </button>
                        )}
                    </div>
                </div>

                <hr className="proposal-divider"/>
            </React.Fragment>
        )
    }

    const payoutTabLabel = opportunity.isFixedFee() ? 'Payout' : (
        opportunity.isFixedRetainer() ? 'Pricing' : 'Availability & Rate'
    )

    return (
        <Proposal
            {...props}
            activeTab={activeTab}
            isDisabled={isDisabled}
            stepHeader={(
                <div className="flex flex-col md:flex-row">
                    <h3 className="text-white text-base md:text-2xl pb-1 md:pb-0">
                        My Proposal
                    </h3>
                    <div className="divider hidden md:flex"/>
                    <p className="whitespace-normal pl-0 md:pl-3 text-white text-sm md:text-lg">
                        <b>
                            Step {activeTabIndex + 1}.
                        </b>

                        <span>
                            {steps[activeTabIndex]}
                        </span>

                    </p>
                </div>
            )}
            // stepHeader={(
            //     <React.Fragment>
            //         <h3>
            //             My Proposal
            //         </h3>
            //         <div className="divider"/>
            //         <p>
            //             <b>
            //                 Step {activeTabIndex + 1}.
            //             </b>

            //             <span>
            //                 {steps[activeTabIndex]}
            //             </span>

            //         </p>
            //     </React.Fragment>
            // )}
            header={false}
            onSetActiveTab={setActiveTab}
            subheader={false}>

            <ProposalSubject>
                {isIndividualOpp ? (
                    <TalentOppQuickSummary
                        job={opportunity.relatedJob()}
                        opportunity={opportunity}
                        isDisabled={isDisabled}
                    />
                ) : (
                    <TeamOppQuickSummary
                        opportunity={opportunity}
                        disabled={isDisabled}
                    />
                )}
            </ProposalSubject>



            {isTeamOpp && (
                <ProposalTab
                    tabKey="Role & Payout"
                    stepNumber={1}
                    completed={completedTab >= 1}>

                    {activeTabIndex === 0 && (
                        <TeamMemberPayoutEditTab
                            showSteps={true}
                            getStepActions={getStepActions}
                            onUpdate={updateOpp}/>
                    )}

                </ProposalTab>
            )}

            <ProposalTab tabKey="Cover Letter"
                         stepNumber={isTeamOpp ? 2 : 1}
                         completed={completedTab >= (isTeamOpp ? 2 :1)}>
                {activeTabIndex === (isTeamOpp ? 1 : 0) && (
                    <CoverLetterEditTab
                        getStepActions={getStepActions}
                        showSteps={true}
                        onUpdate={updateOpp}
                    />
                )}
            </ProposalTab>

            {isIndividualOpp && (
                <ProposalTab tabKey={payoutTabLabel}
                             stepNumber={2}
                             completed={completedTab >= 2}
                             disabled={completedTab <= 1}>
                    {activeTabIndex === 1 && (
                        <React.Fragment>
                            {opportunity.isFixedRetainer() && (
                                <FixedRetainerPayoutEditTab
                                    getStepActions={getStepActions}
                                    showSteps={true}
                                    onUpdate={updateOpp}
                                />
                            )}

                            {opportunity.isFixedFee() && (
                                <FixedFeePayoutEditTab
                                    getStepActions={getStepActions}
                                    showSteps={true}
                                    onUpdate={updateOpp}
                                />
                            )}
                            {opportunity.isTimeMaterials() && (
                                <AvailabilityRateEditTab
                                    getStepActions={getStepActions}
                                    showSteps={true}
                                    onUpdate={updateOpp}
                                />
                            )}
                        </React.Fragment>
                    )}


                </ProposalTab>
            )}

            {isIndividualOpp && (
                <ProposalTab
                    tabKey="My Match Score"
                    stepNumber={3}
                    completed={completedTab >= 3}>

                    {activeTabIndex === 2 && (
                        <MatchScoreEditTab
                            getStepActions={getStepActions}
                        />
                    )}

                </ProposalTab>
            )}

        </Proposal>
    );
};

OppProposalWalkthrough.propTypes = {
    disabled: PropTypes.bool,
    opportunity: PropTypes.any,
    onRequest: PropTypes.any
};

export default OppProposalWalkthrough;

const OppProposalModal = (props) => {
    const {
        onRequest,
        onUpdate,
        opportunity,
        isDisabled,
    } = useOpportunity();

    const {auth} = useAuth();

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const closeProposal = async (relatedJob, skipRedirect) => {
        let params = new URLSearchParams(searchParams);
        params.delete('proposal');
        params.delete('profileReminder');
        params.delete('profileSummary');
        await setSearchParams(params);

        if (props.onClose) {
            props.onClose(relatedJob);
        }
        if (skipRedirect !== true && searchParams.has('returnToProposal')) {
            return navigate(opportunity.proposalLink(), {replace: true})
        }
    }

    const showProfileResumeReminder = async () => {
        await TB.wait(500)
        let params = new URLSearchParams(searchParams);
        params.delete('proposal');
        params.delete('profileSummary');
        params.set('profileReminder', "1");
        return setSearchParams(params);
    }

    const showProfileSummary = async () => {
        await TB.wait(500)
        let params = new URLSearchParams(searchParams);
        params.delete('proposal');
        params.delete('profileReminder');
        params.set('profileSummary', "1");
        return setSearchParams(params);
    }

    if (!auth) {
        return null;
    }
    if (!opportunity) {
        return null;
    }

    return (
        <React.Fragment>
            <Modal
                // getContainer={() => document.getElementById('react-portal-modal-container')}
                zIndex={199}
                footer={null}
                title={null}
                className="job-proposal-modal"
                open={!!opportunity && searchParams.has('proposal')}
                closeable={false}
                maskClosable={false}
                style={{top: 20}}
                wrapClassName="flex flex-col items-stretch px-6"
                destroyOnClose={true}
                width="100%"
                key={`proposal-${opportunity?.project_position?.id}`}
            >

                {opportunity && (
                    <OppProposalWalkthrough
                        showSteps={true}
                        onClose={(relatedJob) => {
                            return closeProposal(relatedJob);
                        }}
                        onComplete={async () => {
                            if (auth?.shouldRemindForPreferencesResumeUpdate()) {
                                return showProfileResumeReminder();
                            }
                            return showProfileSummary()
                        }}
                    />
                )}

            </Modal>
            <Modal
                key={`profileReminder-${opportunity?.project_position?.id}`}
                footer={null}
                title={null}
                className="preferences-update-reminder-modal"
                open={!!opportunity && searchParams.has('profileReminder')}
                closeable={false}
                maskClosable={false}
                style={{top: 20}}
                wrapClassName="flex flex-col items-stretch px-6"
                destroyOnClose={true}
                width="100%">

                {!isDisabled && opportunity?.id && (
                    <JobProfileResumeModal
                        disabled={isDisabled}
                        onRequestClose={() => showProfileSummary()}
                    />
                )}

            </Modal>

            <Modal
                key={`profileSummary-${opportunity?.project_position?.id}`}
                footer={null}
                title={null}
                className="job-match-details-modal"
                open={!!opportunity && searchParams.has('profileSummary')}
                closeable={false}
                maskClosable={false}
                style={{top: 20}}
                wrapClassName="flex flex-col items-stretch px-6"
                destroyOnClose={true}
                width="100%">

                {!isDisabled && opportunity?.id && (
                    <JobProfileSummaryModal
                        disabled={isDisabled}
                        onRequestClose={(skipRedirect) => closeProposal(opportunity.relatedJob?.(), skipRedirect)}
                    />
                )}

            </Modal>
        </React.Fragment>
    )
}

export {
    OppProposalModal
}